import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';

import {
    Icon,
    IconClose,
    IconModerator
} from '../../../base/icons';
import { getBrandingColorPalette } from '../../../base/styles';
import { closeModerationPane } from '../../actions';
import { getModerationPaneOpen } from '../../functions';
import theme from '../../theme.json';
import {
    AntiCollapse,
    ColoredIcon,
    Container,
    Header
} from '../styled';

import AudienceLayoutSection from './AudienceLayoutSection';
import BackstageSection from './BackstageSection';
import RoomControlsSection from './RoomControlsSection';
import VisitorsSection from './VisitorsSection';

declare var interfaceConfig: any;

/**
 * The host controls pane. It docks on the same side as the participants pane
 * and carries the conference-wide moderator controls (visitor mode, backstage,
 * room policies, audience layout) so the participants pane stays a list of
 * people.
 *
 * @returns {ReactElement}
 */
export const ModerationPane = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const paneOpen = useSelector(getModerationPaneOpen);

    const closePane = () => dispatch(closeModerationPane());

    const closePaneKeyPress = e => {
        if (e.key === 'Enter' || e.key === ' ') {
            closePane();
        }
    };

    const panePadding = {
        padding: paneOpen ? '0px 10px 0px 0px' : '0px'
    };
    const headingStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    };
    const iconColor = interfaceConfig.applyMeetingSettings
        ? getBrandingColorPalette(interfaceConfig).accent1
        : '#ffffff';

    return (
        <ThemeProvider theme = { theme }>
            <div
                className = 'participants_pane moderation_pane'
                style = { panePadding }>
                <div className = 'participants_pane-content'>
                    <Header theme = {{ headerBackground: getBrandingColorPalette(interfaceConfig)?.primary }}>
                        <div className = 'participant-pane-heading'>
                            <div style = { headingStyle }>
                                <ColoredIcon color = { iconColor }>
                                    <Icon
                                        className = 'participant_pane-header-icon'
                                        color = { iconColor }
                                        fillColor = { iconColor }
                                        size = { 24 }
                                        src = { IconModerator } />
                                </ColoredIcon>
                                <div className = 'participants_page_heading'>
                                    { t('participantsPane.headings.hostControls') }
                                </div>
                            </div>
                            <div style = { headingStyle }>
                                <Icon
                                    ariaLabel = { t('dialog.close') }
                                    onClick = { closePane }
                                    onKeyPress = { closePaneKeyPress }
                                    role = 'button'
                                    src = { IconClose }
                                    tabIndex = { 0 } />
                            </div>
                        </div>
                    </Header>
                    <Container>
                        <VisitorsSection />
                        <BackstageSection />
                        <RoomControlsSection />
                        <AudienceLayoutSection />
                        <AntiCollapse />
                    </Container>
                </div>
            </div>
        </ThemeProvider>
    );
};

export default ModerationPane;
